import { useContext, useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { MaterialContext } from "../../context/MaterialContext";
import inputStyle from "../../styles/input.module.scss";
import ImageAdd from "../../components/vendor/ImageAdd";
import DetailAdd from "../../components/vendor/DetailAdd";
import ImageAddNewArticle from "../../components/vendor/ImageAddNewArticle";

function MaterialEdit() {
  const params = useParams();
  const navigate = useNavigate();
  const { allMaterials } = useContext(MaterialContext);
  const [materialData, setMaterialData] = useState(null);
  const [materialResolved, setMaterialResolved] = useState(false);
  const [materialArray, setMaterialArray] = useState([]);
  const [materialDescription, setMaterialDescription] = useState("");
  const [workspaceMode, setWorkspaceMode] = useState(() => {
    if (typeof window === "undefined") {
      return "split";
    }

    return (
      window.localStorage.getItem("material-edit-workspace-mode") || "split"
    );
  });
  const [showMaterialMeta, setShowMaterialMeta] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    window.localStorage.setItem("material-edit-workspace-mode", workspaceMode);
  }, [workspaceMode]);

  const handleStartFresh = () => {
    if (typeof window !== "undefined") {
      window.localStorage.removeItem("material-edit-workspace-mode");
    }

    navigate("/material/add");
  };

  useEffect(() => {
    const selectedMaterial = allMaterials.find(
      (mat) => mat.vendor === params.vendor && mat.slug === params.slug,
    );
    setMaterialData(selectedMaterial || null);
    setMaterialResolved(true);
  }, [allMaterials, params.slug, params.vendor]);

  const isExistingMaterial = Boolean(materialData && materialData.id);
  const materialName = (materialData?.title || params.slug || "")
    .toUpperCase()
    .replaceAll("-", " ");

  const materialMeta = {
    id: materialData?.id || "unsaved",
    vendor: (materialData?.vendor || params.vendor || "").toUpperCase(),
    slug: materialData?.slug || params.slug || "",
    status: materialData?.status || "concept",
    added: materialData?.date_added || "-",
    updated: materialData?.date_modified || "-",
  };

  if (!materialResolved) {
    return (
      <div className="content">
        <div className={inputStyle.materialEditWorkflowBar}>
          <h3>Loading material editor...</h3>
        </div>
      </div>
    );
  }

  return (
    <div className="content">
      <div className={inputStyle.materialEditWorkflowBar}>
        <div>
          <h3>
            {isExistingMaterial ? "Editing" : "Creating"}: {materialName}
          </h3>
        </div>

        <div className={inputStyle.materialEditWorkflowControls}>
          <div className={inputStyle.workspaceModeToggle}>
            <button
              type="button"
              className={`${inputStyle.workspaceModeButton} ${workspaceMode === "details" ? inputStyle.workspaceModeButtonActive : ""}`}
              onClick={() => setWorkspaceMode("details")}
            >
              Details
            </button>
            <button
              type="button"
              className={`${inputStyle.workspaceModeButton} ${workspaceMode === "images" ? inputStyle.workspaceModeButtonActive : ""}`}
              onClick={() => setWorkspaceMode("images")}
            >
              Images
            </button>
            <button
              type="button"
              className={`${inputStyle.workspaceModeButton} ${workspaceMode === "split" ? inputStyle.workspaceModeButtonActive : ""}`}
              onClick={() => setWorkspaceMode("split")}
            >
              Split
            </button>
          </div>

          <button
            type="button"
            className={inputStyle.materialMetaToggle}
            onClick={() => setShowMaterialMeta((previous) => !previous)}
            aria-label={
              showMaterialMeta ? "Hide material info" : "Show material info"
            }
            title={
              showMaterialMeta ? "Hide material info" : "Show material info"
            }
          >
            i
          </button>

          <button
            type="button"
            className={inputStyle.materialMetaToggle}
            onClick={handleStartFresh}
            aria-label="Start fresh"
            title="Start fresh"
          >
            ↺
          </button>
        </div>
      </div>

      {showMaterialMeta ? (
        <div className={inputStyle.materialEditMetaPanel}>
          <div>
            <span>ID</span>
            <strong>{materialMeta.id}</strong>
          </div>
          <div>
            <span>Vendor</span>
            <strong>{materialMeta.vendor}</strong>
          </div>
          <div>
            <span>Slug</span>
            <strong>{materialMeta.slug}</strong>
          </div>
          <div>
            <span>Status</span>
            <strong>{materialMeta.status}</strong>
          </div>
          <div>
            <span>Added</span>
            <strong>{materialMeta.added}</strong>
          </div>
          <div>
            <span>Updated</span>
            <strong>{materialMeta.updated}</strong>
          </div>
        </div>
      ) : null}

      <div
        className={`${inputStyle.splitScreen} ${workspaceMode !== "split" ? inputStyle.singleWorkspace : ""}`}
      >
        {workspaceMode !== "images" ? (
          <div
            className={`${inputStyle.inputData} ${workspaceMode === "details" ? inputStyle.workspacePaneFull : ""}`}
          >
            <DetailAdd
              slug={params.slug}
              vendor={params.vendor}
              materialArray={materialArray}
              setMaterialDescription={setMaterialDescription}
            />
          </div>
        ) : null}

        {workspaceMode !== "details" ? (
          <div
            className={`${inputStyle.inputImage} ${workspaceMode === "images" ? inputStyle.workspacePaneFull : ""}`}
          >
            <div className={inputStyle.imagePane}>
              {isExistingMaterial ? (
                <ImageAdd
                  slug={params.slug}
                  vendor={params.vendor}
                  materialDescription={materialDescription}
                  setMaterialArray={setMaterialArray}
                />
              ) : (
                <ImageAddNewArticle
                  slug={params.slug}
                  vendor={params.vendor}
                  materialDescription={materialDescription}
                  setMaterialArray={setMaterialArray}
                />
              )}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default MaterialEdit;
